// src/contexts/HolidayContext.js

/**
 * Holiday Context
 * React Context for holiday state management
 * Provides centralized holiday data and operations for the application
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { Holiday, HolidayServiceError } from '../models/HolidayModels';
import { holidayService } from '../services/HolidayService.js';

// MARK: - Action Types
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_SUCCESS: 'SET_SUCCESS',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  SET_SELECTED_YEAR: 'SET_SELECTED_YEAR',
  SET_HOLIDAYS: 'SET_HOLIDAYS',
  ADD_HOLIDAY: 'ADD_HOLIDAY',
  UPDATE_HOLIDAY: 'UPDATE_HOLIDAY',
  REMOVE_HOLIDAY: 'REMOVE_HOLIDAY',
  ADD_HOLIDAYS_BATCH: 'ADD_HOLIDAYS_BATCH',
  RESET_STATE: 'RESET_STATE'
};

// MARK: - Initial State
const initialState = {
  // Data
  holidays: [],
  selectedYear: new Date().getFullYear(),
  
  // UI State
  isLoading: false,
  errorMessage: '',
  successMessage: '',
  
  // Meta
  lastLoadedCompanyId: null,
  lastLoadedYear: null,
  cacheInfo: {
    lastCacheUpdate: null,
    cacheHitRate: 0
  }
};

// MARK: - Reducer
const holidayReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        errorMessage: action.payload ? '' : state.errorMessage // Clear error when starting new operation
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        isLoading: false,
        errorMessage: action.payload,
        successMessage: ''
      };

    case ACTION_TYPES.SET_SUCCESS:
      return {
        ...state,
        isLoading: false,
        successMessage: action.payload,
        errorMessage: ''
      };

    case ACTION_TYPES.CLEAR_MESSAGES:
      return {
        ...state,
        errorMessage: '',
        successMessage: ''
      };

    case ACTION_TYPES.SET_SELECTED_YEAR:
      return {
        ...state,
        selectedYear: action.payload,
        errorMessage: '',
        successMessage: ''
      };

    case ACTION_TYPES.SET_HOLIDAYS:
      return {
        ...state,
        holidays: action.payload.holidays,
        lastLoadedCompanyId: action.payload.companyId,
        lastLoadedYear: action.payload.year,
        isLoading: false,
        errorMessage: ''
      };

    case ACTION_TYPES.ADD_HOLIDAY:
      return {
        ...state,
        holidays: [...state.holidays, action.payload],
        isLoading: false
      };

    case ACTION_TYPES.UPDATE_HOLIDAY:
      return {
        ...state,
        holidays: state.holidays.map(holiday =>
          holiday.id === action.payload.id ? action.payload : holiday
        ),
        isLoading: false
      };

    case ACTION_TYPES.REMOVE_HOLIDAY:
      return {
        ...state,
        holidays: state.holidays.filter(holiday => holiday.id !== action.payload),
        isLoading: false
      };

    case ACTION_TYPES.ADD_HOLIDAYS_BATCH:
      return {
        ...state,
        holidays: [...state.holidays, ...action.payload],
        isLoading: false
      };

    case ACTION_TYPES.RESET_STATE:
      return {
        ...initialState,
        selectedYear: state.selectedYear // Preserve selected year
      };

    default:
      return state;
  }
};

// MARK: - Context Creation
const HolidayContext = createContext(null);

// MARK: - Custom Hook for Context Access
export const useHolidayContext = () => {
  const context = useContext(HolidayContext);
  if (!context) {
    throw new Error('useHolidayContext must be used within a HolidayProvider');
  }
  return context;
};

// MARK: - Provider Component
export const HolidayProvider = ({ children, companyId }) => {
  const [state, dispatch] = useReducer(holidayReducer, initialState);

  // Auto-clear success messages after 5 seconds
  useEffect(() => {
    if (state.successMessage) {
      const timer = setTimeout(() => {
        dispatch({ type: ACTION_TYPES.CLEAR_MESSAGES });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [state.successMessage]);

  // Auto-load holidays when company or year changes
  useEffect(() => {
    if (companyId && 
        (state.lastLoadedCompanyId !== companyId || 
         state.lastLoadedYear !== state.selectedYear)) {
      loadHolidays();
    }
  }, [companyId, state.selectedYear]);

  // MARK: - Load Holidays
  const loadHolidays = useCallback(async () => {
    if (!companyId) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: 'Company ID is required to load holidays'
      });
      return;
    }

    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

    try {
      console.log(`📅 Loading holidays for ${companyId}, year ${state.selectedYear}`);
      
      const holidays = await holidayService.fetchHolidaysForYear(companyId, state.selectedYear);
      
      dispatch({
        type: ACTION_TYPES.SET_HOLIDAYS,
        payload: {
          holidays: holidays || [],
          companyId,
          year: state.selectedYear
        }
      });

      console.log(`✅ Loaded ${holidays.length} holidays for ${state.selectedYear}`);

    } catch (error) {
      console.error('Failed to load holidays:', error);
      
      let errorMessage = 'Failed to load holidays. Please try again.';
      
      if (error instanceof HolidayServiceError) {
        switch (error.type) {
          case HolidayServiceError.types.NO_INTERNET_CONNECTION:
            errorMessage = 'No internet connection. Showing cached data if available.';
            break;
          case HolidayServiceError.types.UNAUTHORIZED:
            errorMessage = 'You do not have permission to view holidays.';
            break;
          case HolidayServiceError.types.INVALID_COMPANY_ID:
            errorMessage = 'Invalid company configuration.';
            break;
          default:
            errorMessage = error.message;
        }
      }

      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: errorMessage
      });
    }
  }, [companyId, state.selectedYear]);

  // MARK: - Create Holiday
  const createHoliday = useCallback(async (holidayData) => {
    if (!companyId) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: 'Company ID is required to create holidays'
      });
      return false;
    }

    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

    try {
      // Create Holiday instance
      const holiday = new Holiday(holidayData);
      
      // Validate
      const validation = holiday.validate();
      if (!validation.isValid) {
        dispatch({
          type: ACTION_TYPES.SET_ERROR,
          payload: validation.error
        });
        return false;
      }

      // Check for duplicates in current data
      const existingHoliday = state.holidays.find(h => 
        Holiday.isSameDate(h, holiday)
      );
      
      if (existingHoliday) {
        dispatch({
          type: ACTION_TYPES.SET_ERROR,
          payload: `A holiday already exists on ${holiday.dateString}: ${existingHoliday.name}`
        });
        return false;
      }

      console.log(`➕ Creating holiday: ${holiday.name}`);
      
      const createdHoliday = await holidayService.createHoliday(companyId, holiday);
      
      dispatch({ type: ACTION_TYPES.ADD_HOLIDAY, payload: createdHoliday });
      dispatch({
        type: ACTION_TYPES.SET_SUCCESS,
        payload: `Holiday "${createdHoliday.name}" created successfully`
      });

      console.log(`✅ Holiday created: ${createdHoliday.name}`);
      return true;

    } catch (error) {
      console.error('Failed to create holiday:', error);
      
      let errorMessage = 'Failed to create holiday. Please try again.';
      
      if (error instanceof HolidayServiceError) {
        errorMessage = error.message;
      }

      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: errorMessage
      });
      
      return false;
    }
  }, [companyId, state.holidays]);

  // MARK: - Update Holiday
  const updateHoliday = useCallback(async (holidayId, updatedData) => {
    if (!companyId) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: 'Company ID is required to update holidays'
      });
      return false;
    }

    const existingHoliday = state.holidays.find(h => h.id === holidayId);
    if (!existingHoliday) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: 'Holiday not found'
      });
      return false;
    }

    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

    try {
      // Create updated Holiday instance
      const updatedHoliday = new Holiday({
        ...existingHoliday,
        ...updatedData,
        id: holidayId // Ensure ID doesn't change
      });
      
      // Validate
      const validation = updatedHoliday.validate();
      if (!validation.isValid) {
        dispatch({
          type: ACTION_TYPES.SET_ERROR,
          payload: validation.error
        });
        return false;
      }

      console.log(`✏️ Updating holiday: ${updatedHoliday.name}`);
      
      const result = await holidayService.updateHoliday(companyId, updatedHoliday);
      
      dispatch({ type: ACTION_TYPES.UPDATE_HOLIDAY, payload: result });
      dispatch({
        type: ACTION_TYPES.SET_SUCCESS,
        payload: `Holiday "${result.name}" updated successfully`
      });

      console.log(`✅ Holiday updated: ${result.name}`);
      return true;

    } catch (error) {
      console.error('Failed to update holiday:', error);
      
      let errorMessage = 'Failed to update holiday. Please try again.';
      
      if (error instanceof HolidayServiceError) {
        errorMessage = error.message;
      }

      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: errorMessage
      });
      
      return false;
    }
  }, [companyId, state.holidays]);

  // MARK: - Delete Holiday
  const deleteHoliday = useCallback(async (holidayId) => {
    if (!companyId) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: 'Company ID is required to delete holidays'
      });
      return false;
    }

    const holidayToDelete = state.holidays.find(h => h.id === holidayId);
    if (!holidayToDelete) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: 'Holiday not found'
      });
      return false;
    }

    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

    try {
      console.log(`🗑️ Deleting holiday: ${holidayToDelete.name}`);
      
      await holidayService.deleteHoliday(companyId, holidayId);
      
      dispatch({ type: ACTION_TYPES.REMOVE_HOLIDAY, payload: holidayId });
      dispatch({
        type: ACTION_TYPES.SET_SUCCESS,
        payload: `Holiday "${holidayToDelete.name}" deleted successfully`
      });

      console.log(`✅ Holiday deleted: ${holidayToDelete.name}`);
      return true;

    } catch (error) {
      console.error('Failed to delete holiday:', error);
      
      let errorMessage = 'Failed to delete holiday. Please try again.';
      
      if (error instanceof HolidayServiceError) {
        errorMessage = error.message;
      }

      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: errorMessage
      });
      
      return false;
    }
  }, [companyId, state.holidays]);

  // MARK: - Add Common Holidays
  const addCommonHolidays = useCallback(async (locale = 'de') => {
    if (!companyId) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: 'Company ID is required to add holidays'
      });
      return false;
    }

    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

    try {
      console.log(`📦 Adding common holidays for ${state.selectedYear} (${locale})`);
      
      const commonHolidays = Holiday.createCommonHolidays(state.selectedYear, locale);
      
      if (commonHolidays.length === 0) {
        dispatch({
          type: ACTION_TYPES.SET_ERROR,
          payload: 'No common holidays available for this year'
        });
        return false;
      }

      // Filter out holidays that already exist
      const existingDateStrings = new Set(state.holidays.map(h => h.dateString));
      const newHolidays = commonHolidays.filter(h => !existingDateStrings.has(h.dateString));
      
      if (newHolidays.length === 0) {
        dispatch({
          type: ACTION_TYPES.SET_SUCCESS,
          payload: 'All common holidays for this year already exist'
        });
        return true;
      }

      const createdHolidays = await holidayService.batchCreateHolidays(companyId, newHolidays);
      
      dispatch({ type: ACTION_TYPES.ADD_HOLIDAYS_BATCH, payload: createdHolidays });
      dispatch({
        type: ACTION_TYPES.SET_SUCCESS,
        payload: `${createdHolidays.length} common holidays added successfully`
      });

      console.log(`✅ Added ${createdHolidays.length} common holidays`);
      return true;

    } catch (error) {
      console.error('Failed to add common holidays:', error);
      
      let errorMessage = 'Failed to add common holidays. Please try again.';
      
      if (error instanceof HolidayServiceError) {
        errorMessage = error.message;
      }

      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: errorMessage
      });
      
      return false;
    }
  }, [companyId, state.selectedYear, state.holidays]);

  // MARK: - Year Navigation
  const setSelectedYear = useCallback((year) => {
    if (year < 2000 || year > 2100) {
      dispatch({
        type: ACTION_TYPES.SET_ERROR,
        payload: 'Please select a year between 2000 and 2100'
      });
      return;
    }

    dispatch({ type: ACTION_TYPES.SET_SELECTED_YEAR, payload: year });
  }, []);

  const goToPreviousYear = useCallback(() => {
    setSelectedYear(state.selectedYear - 1);
  }, [state.selectedYear, setSelectedYear]);

  const goToNextYear = useCallback(() => {
    setSelectedYear(state.selectedYear + 1);
  }, [state.selectedYear, setSelectedYear]);

  // MARK: - Utility Methods
  const clearMessages = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_MESSAGES });
  }, []);

  const refreshHolidays = useCallback(() => {
    if (companyId) {
      // Clear cache for this company/year and reload
      holidayService._invalidateAllCaches(companyId);
      loadHolidays();
    }
  }, [companyId, loadHolidays]);

  const resetState = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET_STATE });
  }, []);

  // MARK: - Computed Values
  const sortedHolidays = Holiday.sortByDate(state.holidays);
  const hasHolidays = state.holidays.length > 0;
  const isCurrentYear = state.selectedYear === new Date().getFullYear();
  
  // Group holidays by month for easier display
  const holidaysByMonth = sortedHolidays.reduce((acc, holiday) => {
    const month = holiday.date.getMonth();
    if (!acc[month]) {
      acc[month] = [];
    }
    acc[month].push(holiday);
    return acc;
  }, {});

  // Statistics
  const stats = {
    totalHolidays: state.holidays.length,
    paidHolidays: state.holidays.filter(h => h.isPaidHoliday).length,
    unpaidHolidays: state.holidays.filter(h => !h.isPaidHoliday).length,
    monthsWithHolidays: Object.keys(holidaysByMonth).length
  };

  // MARK: - Context Value
  const contextValue = {
    // State
    ...state,
    
    // Computed values
    sortedHolidays,
    hasHolidays,
    isCurrentYear,
    holidaysByMonth,
    stats,
    
    // Actions
    loadHolidays,
    createHoliday,
    updateHoliday,
    deleteHoliday,
    addCommonHolidays,
    
    // Navigation
    setSelectedYear,
    goToPreviousYear,
    goToNextYear,
    
    // Utilities
    clearMessages,
    refreshHolidays,
    resetState
  };

  return (
    <HolidayContext.Provider value={contextValue}>
      {children}
    </HolidayContext.Provider>
  );
};

// MARK: - Export Default
export default HolidayContext;